<template>
  <div class="cognito-reset-password" data-amplify-authenticator>
    <LoadingIndicator v-if="isLoading" />

    <div v-else class="reset-password-container">
      <div class="cognito-reset-password__logo">
        <img
          src="@/assets/img/login_logo.png"
          alt="OU Direct Logo"
          class="oudirect-logo"
        />
      </div>
      <div class="reset-password-header">
        <h1>{{ accountActivation ? "Activate Account" : "Reset Password" }}</h1>
      </div>

      <div class="reset-password-content">
        <!-- Step 1: Request Reset Code -->
        <div v-if="currentStep === 'request'" class="reset-step">
          <p class="instructions">
            Enter your username to receive a password reset code via email.
          </p>

          <div class="form-group">
            <label for="username">Username:</label>
            <input
              id="username"
              v-model="username"
              type="text"
              class="form-input"
              placeholder="Enter your username"
              :disabled="isProcessing"
            />
          </div>

          <div class="button-group">
            <button
              class="amplify-button amplify-field-group__control amplify-button--primary amplify-field-group__control amplify-authenticator__font"
              @click="sendResetCode"
              :disabled="!username.trim() || isProcessing"
            >
              {{ isProcessing ? "Sending..." : "Send Reset Code" }}
            </button>
          </div>
        </div>

        <!-- Step 2: Enter Code and New Password -->
        <div v-if="currentStep === 'confirm'" class="reset-step">
          <p class="instructions">
            Enter the verification code sent to your email and your new
            password.
          </p>

          <div class="form-group">
            <label for="code">Verification Code:</label>
            <input
              id="code"
              v-model="resetCode"
              type="text"
              class="form-input"
              placeholder="Enter verification code"
              :disabled="isProcessing"
            />
          </div>

          <div class="form-group">
            <label for="newPassword">New Password:</label>
            <input
              id="newPassword"
              v-model="newPassword"
              :type="showPasswords ? 'text' : 'password'"
              class="form-input"
              placeholder="Enter new password"
              :disabled="isProcessing"
            />
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirm Password:</label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              :type="showPasswords ? 'text' : 'password'"
              class="form-input"
              placeholder="Confirm new password"
              :disabled="isProcessing"
            />
            <button
              type="button"
              class="show-password-btn"
              @click="showPasswords = !showPasswords"
              :disabled="isProcessing"
            >
              {{ showPasswords ? "Hide password" : "Show password" }}
            </button>
          </div>

          <div class="button-group">
            <button
              class="amplify-button amplify-field-group__control amplify-button--primary amplify-field-group__control amplify-authenticator__font"
              :class="{ 'amplify-button--disabled': isProcessing }"
              @click="confirmResetPassword"
              :disabled="!isFormValid || isProcessing"
            >
              {{ accountActivation ? "Activate Account" : "Reset Password" }}
            </button>
            <button
              class="amplify-button amplify-field-group__control amplify-button--secondary amplify-field-group__control amplify-authenticator__font"
              :class="{ 'amplify-button--disabled': isProcessing }"
              @click="requestNewCode"
              :disabled="isProcessing"
            >
              {{ "Request New Code" }}
            </button>
          </div>
        </div>

        <!-- Success Message -->
        <div v-if="currentStep === 'success'" class="reset-step success">
          <div v-if="accountActivation">
            <h2>Account Activated Successfully!</h2>
            <p>
              Your account has been activated. You can now sign in with your new
              password.
            </p>
          </div>
          <div v-else>
            <h2>Password Reset Successful!</h2>
            <p>
              Your password has been successfully reset. You can now sign in
              with your new password.
            </p>
          </div>

          <div class="button-group">
            <button
              class="amplify-button amplify-field-group__control amplify-button--primary amplify-field-group__control amplify-authenticator__font"
             @click="goToLogin"> Login </button>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <!-- Back to Login Link -->
        <div v-if="currentStep !== 'success'" class="back-to-login">
          <button
            class="amplify-button amplify-field-group__control amplify-button--link amplify-button--small amplify-field-group__control amplify-authenticator__font"
            @click="goToLogin"
            href="#"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import {
  resetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
} from "@aws-amplify/auth";
import LoadingIndicator from "./LoadingIndicator.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { Pages } from "@/pages/pages";
import { useQueryParams } from "@/shared/helpers/useQueryParams";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import "@aws-amplify/ui-vue/styles.css";
import "@/assets/cognito-overrides.scss";

type ResetStep = "request" | "confirm" | "success";

const isLoading = ref(false);
const isProcessing = ref(false);
const currentStep = ref<ResetStep>("request");
const errorMessage = ref("");

const username = ref("");
const accountActivation = ref(false);
const resetCode = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const showPasswords = ref(false);

const { getParam } = useQueryParams();

const isFormValid = computed(() => {
  return (
    resetCode.value.trim() &&
    newPassword.value.trim() &&
    confirmPassword.value.trim() &&
    newPassword.value === confirmPassword.value &&
    newPassword.value.length >= 8
  );
});

const sendResetCode = async () => {
  if (!username.value.trim()) {
    errorMessage.value = "Please enter your username.";
    return;
  }

  try {
    isProcessing.value = true;
    errorMessage.value = "";

    await resetPassword({ username: username.value.trim() });

    currentStep.value = "confirm";
  } catch (error: any) {
    console.error("Error sending reset code:", error);
    errorMessage.value =
      error.message || "Failed to send a code. Please try again.";
  } finally {
    isProcessing.value = false;
  }
};

const confirmResetPassword = async () => {
  if (!isFormValid.value) {
    errorMessage.value =
      "Please fill in all fields correctly. Password must be at least 8 characters.";
    return;
  }

  try {
    isProcessing.value = true;
    errorMessage.value = "";

    await amplifyConfirmResetPassword({
      username: username.value.trim(),
      confirmationCode: resetCode.value.trim(),
      newPassword: newPassword.value,
    });

    currentStep.value = "success";
  } catch (error: any) {
    console.error("Error confirming reset password:", error);
    errorMessage.value =
      error.message ||
      "Failed to reset password. Please check your code and try again.";
  } finally {
    isProcessing.value = false;
  }
};

const requestNewCode = async () => {
  try {
    isProcessing.value = true;
    errorMessage.value = "";

    await resetPassword({ username: username.value.trim() });

    // Clear the old code
    resetCode.value = "";
  } catch (error: any) {
    console.error("Error requesting new code:", error);
    errorMessage.value =
      error.message || "Failed to send new code. Please try again.";
  } finally {
    isProcessing.value = false;
  }
};

const goToLogin = () => {
  window.location.href = Pages.Login;
};

const parseUrlParameters = () => {
  resetCode.value = getParam("code", "");
  username.value = getParam("username", "");
  accountActivation.value = getParam("accountActivation") == "true";
  if (username.value) currentStep.value = "confirm";
};

onMounted(() => {
  parseUrlParameters();
});
</script>

<style scoped lang="scss">
.cognito-reset-password {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;

  &__logo {
    text-align: center;
    margin-bottom: 20px;
  }

  .amplify-button {
    width: 100%;
  }
}

.reset-password-container {
  max-width: 450px;
  margin: 50px auto;
  padding: 30px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.reset-password-header {
  margin-bottom: 16px;
}

.reset-password-header h1 {
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.reset-password-content {
  text-align: left;
}

.reset-step {
  margin-bottom: 20px;
}

.reset-step.success {
  text-align: center;
}

.reset-step.success h2 {
  color: #28a745;
  margin-bottom: 15px;
}

.instructions {
  margin-bottom: 20px;
  color: #666;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #333;
  font-weight: 500;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;
  transition: border-color 0.3s;
}

.form-input:focus {
  outline: none;
  border-color: #007bff;
}

.form-input:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
}

.show-password-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 16px;
  margin-top: 8px;
}

.button-group {
  margin-top: 25px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
}

.error-message {
  color: #dc3545;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 12px;
  margin-top: 15px;
  margin-bottom: 15px;
}

.back-to-login {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.link {
  color: #007bff;
  text-decoration: none;
  cursor: pointer;
}

.link:hover {
  text-decoration: underline;
}
</style>
